import { selector } from 'recoil';
import {
  getForecasts,
  getForecastFilterParams,
  getBookingTemplates,
  getForecastByRegion as getForecastByRegionApi,
  listWarehouse,
  getForecastStatisticDataByRegion as getForecastStatisticDataByRegionApi,
} from '../../repositories/HttpRepository';
import _ from 'lodash';
import { OWNER_TYPE_MAIN, OWNER_TYPE_MOBILE } from '../../containers/Warehouse/constants';
import { mergeStatisticToForecastData } from '../../containers/ForecastManager/utils/mergeStatisticToForecastData';

// eslint-disable-next-line import/named
import { currentForecastState, forecastFiltersState, selectedRegionState } from './state';

// eslint-disable-next-line import/prefer-default-export
export const getForecastList = selector({
  key: 'selector/forecast/list',
  get: async ({ get }) => {
    // get filters first
    const filters = get(forecastFiltersState);
    const response = await getForecasts(filters);

    const sortByRegion = (t) => t.regions;
    const weekDaySort = {
      MON: 2,
      TUE: 3,
      WED: 4,
      THU: 5,
      FRI: 6,
      SAT: 7,
      SUN: 8,
    };

    const sortByWeekDay = (t) => _.get(weekDaySort, t.week_day, 0);

    const data = response.data;
    if (data.items) {
      data.items = _.orderBy(data.items, [sortByRegion, sortByWeekDay]);
    }

    return data;
  },
});

export const getTemplates = selector({
  key: 'selector/forecast/templates',
  get: async ({ get }) => {
    // get filters first
    const regions = get(currentForecastState)?.regions || [];
    if (_.isEmpty(regions)) return [];

    const response = await getBookingTemplates({ regions });
    return response.data?.items;
  },
});

export const getFilterParams = selector({
  key: 'selector/forecast/filter-params',
  get: async () => {
    // get filters first
    const response = await getForecastFilterParams();
    return response.data;
  },
});

// Base selector - fetch both forecast and statistic data once
// Keep only the current region cached: data of other regions is dropped, so selecting one again always refetches
export const forecastAndStatisticData = selector({
  key: 'selector/forecast/base-data',
  cachePolicy_UNSTABLE: { eviction: 'most-recent' }, // like stale time 0 in react-query
  get: async ({ get }) => {
    const region = get(selectedRegionState);
    if (!region) return { forecast: [], statistic: [] };

    const [response, statisticResponse] = await Promise.all([getForecastByRegionApi(region), getForecastStatisticDataByRegionApi(region)]);

    if (!response.ok || !statisticResponse.ok) return { forecast: [], statistic: [] };

    return {
      forecast: response.data || [],
      statistic: statisticResponse.data || [],
    };
  },
});

// Selector to get forecast data with statistic merged
export const getForecastByRegion = selector({
  key: 'selector/forecast/by/region',
  cachePolicy_UNSTABLE: { eviction: 'most-recent' },
  get:({ get }) => {
    const { forecast, statistic } = get(forecastAndStatisticData);
    if (!forecast.length) return [];
    return mergeStatisticToForecastData(forecast, statistic);
  },
});

// Selector to get statistic data
export const getStatisticForecast = selector({
  key: 'selector/forecast/statistic',
  cachePolicy_UNSTABLE: { eviction: 'most-recent' },
  get:({ get }) => {
    const { statistic } = get(forecastAndStatisticData);
    const statisticItem = statistic?.[0];
    return {
      numberOfWeeks: statisticItem?.numberOfWeeks,
      percentage: statisticItem?.percentage,
    };
  },
});

export const getWarehousesByRegion = selector({
  key: 'selector/forecast/warehouses/by/region',
  get: async ({ get }) => {
    const region = get(selectedRegionState);
    if (!region) return [];

    const params = new URLSearchParams();
    params.append('regions', region);
    params.append('owner_types', OWNER_TYPE_MAIN);
    params.append('owner_types', OWNER_TYPE_MOBILE);
    params.append('size', 1000); // to fetch all warehouses
    params.append('order_by', 'id');
    params.append('desc', 'false');

    const response = await listWarehouse(params);
    if (!response.ok) return [];
    return response.data?.items || [];
  },
});
